import { DraftDataDto, UserProfileDto, UserRole } from "@green0meter/api-client";
import {
  QueryObserverResult,
  RefetchOptions,
  useQueries,
  useQuery,
  UseQueryResult,
} from "@tanstack/react-query";

import { DOMAIN_FORM_SUBMISSIONS } from "../domain";

import { submissionsApi } from "@api/services";
import { TANSTACK_QUERY_STALE_TIME } from "@constants";
import { roleGuard } from "@api";

export const ACTION_GET_FORM_DRAFT_DATA = "GET_FORM_DRAFT_DATA";

const allowedRoles = [
  UserRole.Owner,
  UserRole.Manager,
  UserRole.Respondent,
  UserRole.Viewer,
  UserRole.Auditor,
];

export const fetchFormDraftData = async (
  formId: string,
): Promise<DraftDataDto[] | undefined> => {
  const response =
    await submissionsApi.submissionsControllerGetFormDraftData(formId);

  return response.data;
};

export const useGetFormDraftData = (
  user: UserProfileDto | null,
  formId: string,
  enable: boolean = true,
): {
  formDraftData?: DraftDataDto[];
  formDraftDataEnabled: boolean;
  formDraftDataError: Error | null;
  isLoadingFormDraftData: boolean;
  isFetchingFormDraftData: boolean;
  isPendingFormDraftData: boolean;
  isSuccessFormDraftData: boolean;
  refetchFormDraftData: (options?: RefetchOptions) => Promise<QueryObserverResult>;
} => {
  const enabled = enable && roleGuard(user, allowedRoles);

  const { data, error, isLoading, isFetching, isPending, isSuccess, refetch } =
    useQuery<DraftDataDto[] | undefined, Error>({
      queryKey: [DOMAIN_FORM_SUBMISSIONS, ACTION_GET_FORM_DRAFT_DATA, formId],
      queryFn: () => fetchFormDraftData(formId),
      staleTime: TANSTACK_QUERY_STALE_TIME,
      refetchOnWindowFocus: true,
      enabled,
    });

  return {
    formDraftData: data,
    formDraftDataEnabled: enabled,
    formDraftDataError: error,
    isLoadingFormDraftData: isLoading,
    isFetchingFormDraftData: isFetching,
    isPendingFormDraftData: isPending,
    isSuccessFormDraftData: isSuccess,
    refetchFormDraftData: refetch,
  };
};

export const useGetMultipleFormsDraftData = (
  user: UserProfileDto | null,
  formIds: string[],
  enable: boolean = true,
): {
  formsDraftData: UseQueryResult<DraftDataDto[] | undefined, Error>[];
  formsDraftDataEnabled: boolean;
  allFormsDraftDataFetched: boolean;
} => {
  const enabled = enable && roleGuard(user, allowedRoles);

  const formsDraftData = useQueries({
    queries: formIds.map((formId) => ({
      queryKey: [DOMAIN_FORM_SUBMISSIONS, ACTION_GET_FORM_DRAFT_DATA, formId],
      queryFn: () => fetchFormDraftData(formId),
      enabled,
      staleTime: TANSTACK_QUERY_STALE_TIME,
    })),
  });

  const allFormsDraftDataFetched = formsDraftData.every((query) => query.isSuccess);

  return {
    formsDraftData,
    formsDraftDataEnabled: enabled,
    allFormsDraftDataFetched,
  };
};
